import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where } from 'firebase/firestore';
import { FaUserPlus, FaEdit, FaTrash, FaSpinner, FaSearch, FaPhone, FaEnvelope, FaUserCircle } from 'react-icons/fa';

const CompanyCustomersPage = () => {
    const { userData } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Form State
    const [formData, setFormData] = useState({ name: '', phone: '', email: '', notes: '' });
    const [editingId, setEditingId] = useState(null);

    const customersCollectionRef = useMemo(() => {
        if (!userData?.companyProfileId) return null;
        return collection(db, 'companies', userData.companyProfileId, 'customers');
    }, [userData]);

    const fetchData = useCallback(async () => {
        if (!customersCollectionRef || !userData?.companyProfileId) return;
        setLoading(true);

        try {
            // 1. Fetch Customers
            const qCustomers = query(customersCollectionRef, orderBy('name'));
            const customerSnap = await getDocs(qCustomers);
            const customerData = customerSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

            // 2. Fetch All Rentals for this Company (to calculate stats)
            // Note: In a massive app, we'd paginate or use cloud functions. For this scale, client-side aggregation is fine.
            const qRentals = query(collection(db, 'rentals'), where('companyId', '==', userData.companyProfileId));
            const rentalSnap = await getDocs(qRentals);
            const rentalData = rentalSnap.docs.map(doc => doc.data());

            // 3. Merge Stats into Customers
            const enhancedCustomers = customerData.map(cust => {
                // Find rentals matching this customer's name (or ID if we had a strict link, but usually it's by name/phone)
                // Assuming simple name matching for now as per likely existing data structure
                const customerRentals = rentalData.filter(r => 
                    (r.renterName && r.renterName.toLowerCase() === cust.name.toLowerCase()) || 
                    (cust.phone && r.renterPhone === cust.phone)
                );

                const totalSpent = customerRentals.reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0);
                
                // Find latest rental
                let lastRentalDate = null;
                if (customerRentals.length > 0) {
                    const sortedRentals = customerRentals.sort((a, b) => (b.rentalStartDate?.seconds || 0) - (a.rentalStartDate?.seconds || 0));
                    if (sortedRentals[0].rentalStartDate) {
                        lastRentalDate = new Date(sortedRentals[0].rentalStartDate.seconds * 1000);
                    }
                }

                return {
                    ...cust,
                    totalSpent,
                    lastRentalDate,
                    rentalCount: customerRentals.length
                };
            });

            setCustomers(enhancedCustomers);

        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setLoading(false);
        }
    }, [customersCollectionRef, userData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                const customerDoc = doc(customersCollectionRef, editingId);
                await updateDoc(customerDoc, formData);
            } else {
                await addDoc(customersCollectionRef, { ...formData, createdAt: new Date() });
            }
            setFormData({ name: '', phone: '', email: '', notes: '' });
            setEditingId(null);
            fetchData();
        } catch (error) {
            console.error("Error saving customer:", error);
            alert("Kaydetme hatası");
        }
    };

    const handleEdit = (customer) => {
        setFormData({ name: customer.name || '', phone: customer.phone || '', email: customer.email || '', notes: customer.notes || '' });
        setEditingId(customer.id);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm("Bu müşteriyi silmek istediğinizden emin misiniz?")) {
            await deleteDoc(doc(customersCollectionRef, id));
            fetchData();
        }
    };

    // Filtering
    const filteredCustomers = customers.filter(c => 
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.phone || '').includes(searchTerm) ||
        (c.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 mb-8">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 max-w-7xl">
                    <h1 className="text-3xl font-bold text-gray-900">Müşteri Yönetimi</h1>
                    <p className="text-gray-500 mt-1">Müşteri veritabanı ve kiralama geçmişi</p>
                </div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                    
                    {/* Left Column: Form */}
                    <div className="xl:col-span-1">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 sticky top-24">
                            <h2 className="text-xl font-bold mb-6 flex items-center text-gray-800">
                                {editingId ? <FaEdit className="mr-2 text-blue-600"/> : <FaUserPlus className="mr-2 text-blue-600"/>}
                                {editingId ? 'Müşteri Düzenle' : 'Yeni Müşteri Ekle'}
                            </h2>
                            <form onSubmit={handleSave} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                                    <input 
                                        type="text" 
                                        placeholder="Örn: Ahmet Yılmaz" 
                                        value={formData.name} 
                                        onChange={e => setFormData({...formData, name: e.target.value})} 
                                        required 
                                        className="w-full border-gray-300 border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                                    <input 
                                        type="tel" 
                                        placeholder="0555 555 55 55" 
                                        value={formData.phone} 
                                        onChange={e => setFormData({...formData, phone: e.target.value})} 
                                        className="w-full border-gray-300 border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                                    <input 
                                        type="email" 
                                        placeholder="ahmet@ornek.com" 
                                        value={formData.email} 
                                        onChange={e => setFormData({...formData, email: e.target.value})} 
                                        className="w-full border-gray-300 border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                                    <textarea 
                                        placeholder="Müşteri hakkında özel notlar..." 
                                        value={formData.notes} 
                                        onChange={e => setFormData({...formData, notes: e.target.value})} 
                                        className="w-full border-gray-300 border rounded-xl p-3 focus:ring-2 focus:ring-blue-500 transition" 
                                        rows="3"
                                    ></textarea>
                                </div>
                                <div className="pt-2 flex gap-3">
                                    <button type="submit" className="flex-1 bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition shadow-lg">
                                        {editingId ? 'Güncelle' : 'Kaydet'}
                                    </button>
                                    {editingId && (
                                        <button 
                                            type="button" 
                                            onClick={() => { setEditingId(null); setFormData({ name: '', phone: '', email: '', notes: '' });}} 
                                            className="px-6 bg-gray-100 text-gray-600 py-3 rounded-xl font-semibold hover:bg-gray-200 transition"
                                        >
                                            İptal
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Right Column: List */}
                    <div className="xl:col-span-2">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            {/* Toolbar */}
                            <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
                                <h3 className="text-xl font-bold text-gray-800">Müşteri Listesi ({filteredCustomers.length})</h3>
                                <div className="relative w-full sm:w-64">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"/>
                                    <input 
                                        type="text" 
                                        placeholder="Müşteri ara..." 
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                    />
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-20">
                                    <FaSpinner className="animate-spin inline-block text-3xl text-blue-600 mb-4"/>
                                    <p className="text-gray-500">Müşteriler yükleniyor...</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                            <tr>
                                                <th className="px-6 py-4 font-semibold">Müşteri</th>
                                                <th className="px-6 py-4 font-semibold">İletişim</th>
                                                <th className="px-6 py-4 font-semibold text-center">Toplam Harcama</th>
                                                <th className="px-6 py-4 font-semibold text-center">Son Kiralama</th>
                                                <th className="px-6 py-4 font-semibold text-right">İşlem</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {filteredCustomers.length > 0 ? filteredCustomers.map(customer => (
                                                <tr key={customer.id} className="hover:bg-gray-50 transition group">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center">
                                                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 text-blue-600 flex items-center justify-center font-bold text-lg mr-3">
                                                                {customer.name ? customer.name.charAt(0).toUpperCase() : <FaUserCircle/>}
                                                            </div>
                                                            <div>
                                                                <p className="font-bold text-gray-900">{customer.name}</p>
                                                                <p className="text-xs text-gray-500 truncate max-w-[150px]">{customer.notes}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="space-y-1">
                                                            {customer.phone && (
                                                                <div className="flex items-center text-sm text-gray-600">
                                                                    <FaPhone className="mr-2 text-gray-400" size={12}/> {customer.phone}
                                                                </div>
                                                            )}
                                                            {customer.email && (
                                                                <div className="flex items-center text-sm text-gray-600">
                                                                    <FaEnvelope className="mr-2 text-gray-400" size={12}/> {customer.email}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        {customer.totalSpent > 0 ? (
                                                            <span className="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-bold border border-green-100">
                                                                {customer.totalSpent.toLocaleString('tr-TR')} ₺
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-400 text-sm">-</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        {customer.lastRentalDate ? (
                                                            <div className="text-sm text-gray-700">
                                                                {customer.lastRentalDate.toLocaleDateString()}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs">Hiç kiralamadı</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right whitespace-nowrap">
                                                        <button 
                                                            onClick={() => handleEdit(customer)} 
                                                            className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition mr-2" 
                                                            title="Düzenle"
                                                        >
                                                            <FaEdit/>
                                                        </button>
                                                        <button 
                                                            onClick={() => handleDelete(customer.id)} 
                                                            className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition" 
                                                            title="Sil"
                                                        >
                                                            <FaTrash/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-10 text-gray-500">
                                                        Kayıt bulunamadı.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyCustomersPage;